import { createContext, useContext, useCallback, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";

// PATCH 36 Me10 — one confirmation-dialog pattern for every destructive action (delete/reject/revoke).
// Usage: const confirm = useConfirm(); if (await confirm({ title, body, confirmLabel, tone: "danger" })) {...}
const ConfirmCtx = createContext(() => Promise.resolve(true));

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setState({
      title: opts.title || "Are you sure?",
      body: opts.body || "This action can't be undone.",
      confirmLabel: opts.confirmLabel || "Confirm",
      cancelLabel: opts.cancelLabel || "Cancel",
      tone: opts.tone || "danger",
    });
  }), []);

  const close = (val) => { setState(null); resolver.current?.(val); resolver.current = null; };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <div data-testid="confirm-dialog" role="dialog" aria-modal="true"
          onClick={() => close(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(17,17,19,.6)", zIndex: 90,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div className="k-container k-fadein" onClick={(e) => e.stopPropagation()} style={{ width: 420, padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              {state.tone === "danger" && <AlertTriangle size={18} aria-hidden="true" />}
              <h3 className="k-heading" style={{ margin: 0, fontSize: 17 }}>{state.title}</h3>
            </div>
            <p className="k-muted" style={{ fontSize: 13, lineHeight: 1.6, marginTop: 0 }}>{state.body}</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <button className="k-btn k-btn-ghost" data-testid="confirm-cancel" onClick={() => close(false)}>{state.cancelLabel}</button>
              <button className={`k-btn ${state.tone === "danger" ? "k-btn-danger" : "k-btn-primary"}`}
                data-testid="confirm-accept" onClick={() => close(true)}>{state.confirmLabel}</button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmCtx);
